import { Link, Navigate } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import MenuSheet from "@/components/MenuSheet";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft,
  Star,
  Mail,
  MailCheck,
  User as UserIcon,
  Calendar,
  Trash2,
  ShieldCheck,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

function fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function StarsDisplay({ value }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          strokeWidth={1.5}
          className={`size-4 ${
            n <= value ? "fill-amber-400 text-amber-500" : "text-muted-foreground/30"
          }`}
        />
      ))}
    </div>
  );
}

export default function AdminReviewsPage() {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reviews/admin");
      setReviews(data);
    } catch (e) {
      toast.error("Couldn't load reviews");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user && user.role === "admin") load();
  }, [user, load]);

  const stats = useMemo(() => {
    if (reviews.length === 0) return { count: 0, avg: 0, dist: [0, 0, 0, 0, 0] };
    const sum = reviews.reduce((s, r) => s + (r.rating || 0), 0);
    const dist = [0, 0, 0, 0, 0];
    reviews.forEach((r) => {
      if (r.rating >= 1 && r.rating <= 5) dist[r.rating - 1]++;
    });
    return {
      count: reviews.length,
      avg: (sum / reviews.length).toFixed(2),
      dist,
    };
  }, [reviews]);

  const remove = async (id) => {
    await api.delete(`/reviews/${id}`);
    toast.success("Review deleted");
    load();
  };

  const [testEmailTo, setTestEmailTo] = useState(user?.email || "");
  const [sendingTest, setSendingTest] = useState(false);
  const sendTestEmail = async () => {
    if (!testEmailTo) return;
    setSendingTest(true);
    try {
      const { data } = await api.post("/admin/test-email", { to: testEmailTo });
      toast.success("Test email sent", { description: `id ${data.message_id}` });
    } catch (e) {
      toast.error("Test email failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSendingTest(false);
    }
  };

  if (user === null) return null;
  if (!user || user.role !== "admin") {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen">
      <div className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <MenuSheet />
          <Button variant="ghost" size="sm" asChild className="rounded-xl" data-testid="admin-back-button">
            <Link to="/">
              <ArrowLeft strokeWidth={1.5} className="size-4 mr-1.5" />
              Back to planner
            </Link>
          </Button>
        </div>

        {/* Title */}
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            <ShieldCheck strokeWidth={1.5} className="size-3.5" />
            Admin · reviews
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">
            What people are saying
          </h1>
          <p className="text-base text-muted-foreground">
            All user-submitted reviews of Lumivera. Visible only to admins.
          </p>
        </div>

        {/* Email diagnostics */}
        <Card className="rounded-2xl" data-testid="admin-email-test-card">
          <CardHeader>
            <CardTitle className="text-lg font-display flex items-center gap-2">
              <MailCheck strokeWidth={1.5} className="size-5 text-primary" />
              Email deliverability test
            </CardTitle>
            <CardDescription>
              Sends a small smoke-test email through Resend. Use this after DNS changes or to verify
              ongoing deliverability without triggering a real password reset.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                type="email"
                value={testEmailTo}
                onChange={(e) => setTestEmailTo(e.target.value)}
                placeholder="recipient@example.com"
                className="rounded-xl"
                data-testid="admin-email-test-input"
              />
              <Button
                onClick={sendTestEmail}
                disabled={sendingTest || !testEmailTo}
                className="rounded-xl shrink-0"
                data-testid="admin-email-test-send"
              >
                <Mail strokeWidth={1.5} className="size-4 mr-1.5" />
                {sendingTest ? "Sending…" : "Send test email"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <Card className="rounded-2xl">
          <CardContent className="pt-6">
            <div className="flex items-end justify-between gap-6 flex-wrap">
              <div>
                <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                  Total reviews
                </div>
                <div className="font-display text-4xl font-extrabold mt-1" data-testid="admin-reviews-count">
                  {stats.count}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                  Average rating
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="font-display text-4xl font-extrabold" data-testid="admin-reviews-avg">
                    {stats.avg}
                  </span>
                  <Star strokeWidth={1.5} className="size-6 fill-amber-400 text-amber-500" />
                </div>
              </div>
              <div className="flex-1 min-w-[200px]">
                {[5, 4, 3, 2, 1].map((n) => {
                  const count = stats.dist[n - 1];
                  const pct = stats.count === 0 ? 0 : (count / stats.count) * 100;
                  return (
                    <div key={n} className="flex items-center gap-2 text-xs">
                      <span className="w-3 font-semibold">{n}</span>
                      <Star strokeWidth={1.5} className="size-3 fill-amber-400 text-amber-500" />
                      <div className="flex-1 h-1.5 rounded-full bg-secondary overflow-hidden">
                        <div
                          className="h-full bg-amber-400"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className="w-6 text-muted-foreground text-right">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Reviews list */}
        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle className="text-lg font-display">All reviews</CardTitle>
            <CardDescription>
              {loading ? "Loading…" : `${reviews.length} review${reviews.length === 1 ? "" : "s"}`}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {reviews.length === 0 && !loading && (
              <p className="text-sm text-muted-foreground py-4">
                No reviews yet. Once users leave reviews, they'll appear here.
              </p>
            )}
            {reviews.map((r) => (
              <div
                key={r.id}
                data-testid={`admin-review-${r.id}`}
                className="rounded-2xl border border-border bg-card p-4 sm:p-5"
              >
                <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <UserIcon strokeWidth={1.5} className="size-4 text-muted-foreground" />
                      <span className="font-semibold">{r.user_name || "Anonymous"}</span>
                    </div>
                    <div className="text-xs text-muted-foreground flex items-center gap-3 mt-1 flex-wrap">
                      <span className="inline-flex items-center gap-1">
                        <Mail strokeWidth={1.5} className="size-3" />
                        {r.user_email}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <Calendar strokeWidth={1.5} className="size-3" />
                        {fmtDate(r.created_at)}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <StarsDisplay value={r.rating} />
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="rounded-xl size-9 text-muted-foreground hover:text-destructive"
                          data-testid={`admin-review-delete-${r.id}`}
                          aria-label="Delete review"
                        >
                          <Trash2 strokeWidth={1.5} className="size-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete this review?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This can't be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Keep it</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => remove(r.id)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
                {r.text && r.text.trim() && (
                  <p className="text-sm sm:text-base whitespace-pre-wrap leading-relaxed">
                    {r.text}
                  </p>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
